import { Option, Select, useTheme } from "@deere/fuel-react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import React, { ChangeEvent, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import DummyOrderCreationPage from "./DummyOrderCreationPage/DummyOrderCreationPage";
import { OrderType, urlParamsToOrderTypeMapping } from "./types";
import useXtremeBffApi from "../../api/xtremeBff/useXtremeBff";
import {
  createDealerAccountMap,
  createDisplayNameForDealer,
  showDealerName,
} from "../../components/Layout/components/Header/components/SelectDealerAccount/utils";
import { ConfigContext } from "../../context/ConfigContext";
import {
  DealerAccountContext,
  dealerInfoLoader,
} from "../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { PreferenceContext } from "../../context/PreferencesContext/PreferencesContext";
import i18n from "../../i18n/i18n";
import { CategoryKey, ToggleKey } from "../featureToggles/constants";

const SelectAccountForOrder = () => {
  const theme = useTheme();
  const {
    XTREME_BFF_SERVICE_URL,
    ENABLE_ACCOUNT_LOCATION_FALLBACK,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
  } = useContext(ConfigContext);
  const {
    mappedAccounts,
    selectedAccounts,
    updateSelectedAccounts,
    reloadDealerAccount,
  } = useContext(DealerAccountContext);
  const { isToggleEnabledWithAccountId } = useContext(FeatureToggleContextV2);
  const { userPreferences } = useContext(PreferenceContext);

  const shouldDisplayDealerName = showDealerName(userPreferences);

  const isDealerAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;
  const bffApiHook = useXtremeBffApi();
  const { oktaAuth } = useOktaAuth();
  const getAccessToken = (): string => {
    const authToken = oktaAuth.getAccessToken();
    if (!authToken) return "";
    return authToken;
  };

  const { t } = useTranslation(["orders", "common"]);
  const navigate = useNavigate();
  const { orderType: orderTypeInUrl } = useParams<{ orderType: string }>();
  const orderType = urlParamsToOrderTypeMapping[
    orderTypeInUrl as string
  ] as OrderType;

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [isContinueButtonDisable, setContinueButtonDisable] = useState(true);
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState<string>(
    t("select.account.order.creation.helperText") as string
  );

  const accountsToDisplay =
    selectedAccounts?.length > 1 ? selectedAccounts : mappedAccounts;
  const accountsMap = createDealerAccountMap(accountsToDisplay);
  const options = Array.from(accountsMap.values());

  const setErrorAndHelperText = (tKey: string) => {
    const errorMessage = t(tKey);
    setError(true);
    setHelperText(errorMessage);
  };

  const isOrderCreationEnabled = async (
    creationOrderType: OrderType,
    accountId: string,
    country: string
  ): Promise<boolean> => {
    switch (creationOrderType) {
      case OrderType.EMERGENCY_ORDER: {
        const isEmergencyOrderDisabled = await isToggleEnabledWithAccountId(
          CategoryKey.ORDER_SERVICE,
          ToggleKey.DISABLE_EMERGENCY_ORDER,
          country,
          accountId
        );

        return !isEmergencyOrderDisabled;
      }

      case OrderType.STOCK_ORDER: {
        const isStockOrderDisabled = await isToggleEnabledWithAccountId(
          CategoryKey.ORDER_SERVICE,
          ToggleKey.DISABLE_STOCK_ORDER,
          country,
          accountId
        );
        return !isStockOrderDisabled;
      }

      case OrderType.PROMOTION_ORDER: {
        return isToggleEnabledWithAccountId(
          CategoryKey.ORDER_SERVICE,
          ToggleKey.ENABLE_PROMOTION_ORDER,
          country,
          accountId
        );
      }

      default: {
        return false;
      }
    }
  };

  const handleAccountChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const accountId = event.target.value;
    setContinueButtonDisable(true);
    setSelectedAccountId(accountId);

    const country = accountsMap.get(accountId)?.location.country!;
    const isOrderCreationEnabledForAccount = await isOrderCreationEnabled(
      orderType,
      accountId,
      country
    );

    if (!isOrderCreationEnabledForAccount) {
      setErrorAndHelperText("orders:error.account.order.creation.disabled");
      return;
    }

    const dealerAccounts = await dealerInfoLoader(
      XTREME_BFF_SERVICE_URL,
      [accountId],
      getAccessToken(),
      i18n.language,
      ENABLE_ACCOUNT_LOCATION_FALLBACK,
      isDealerAuthEnabled,
      bffApiHook
    );
    const { underEmbargo, overAllBlock, dealerInvalid, markedForDeletion } =
      dealerAccounts[0];

    switch (true) {
      case underEmbargo === "F": {
        setErrorAndHelperText("error.invalidGeoLocation.description");
        break;
      }

      case overAllBlock:
      case dealerInvalid:
      case markedForDeletion: {
        setErrorAndHelperText("error.account.invalid.description");
        break;
      }

      default: {
        setError(false);
        setHelperText(t("select.account.order.creation.helperText") as string);
        setContinueButtonDisable(false);
      }
    }
  };

  const handleCloseModel = () => {
    navigate(`/home`);
  };

  const handleOnClickContinue = () => {
    const selectedAccount = accountsMap.get(selectedAccountId);

    if (selectedAccount && reloadDealerAccount) {
      updateSelectedAccounts([selectedAccount]);
      reloadDealerAccount(selectedAccountId);
      navigate(`/orders/create/${orderTypeInUrl}`);
    }
  };

  return (
    <DummyOrderCreationPage orderType={orderType}>
      <Dialog open fullWidth maxWidth="sm">
        <DialogTitle>Select an Account</DialogTitle>
        <IconButton
          size="large"
          data-testid="close-icon-button"
          sx={{
            position: "absolute",
            right: 15,
            top: 15,
          }}
          onClick={handleCloseModel}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pb: 0 }}>
          <DialogContentText py={2}>
            Please select an account to proceed with the creation.
          </DialogContentText>
          <FormControl fullWidth error={error} sx={{ pb: 3, pt: 4 }}>
            <InputLabel
              color="secondary"
              id="select-account-label"
              sx={{
                color: theme.tokens.FuelColorTextSecondary,
                fontSize: theme.tokens.FuelFontSizeSm,
              }}
            >
              Choose an Account
            </InputLabel>
            <Box sx={{ mt: 2 }}>
              <Select
                data-testid="select-account"
                id="select-account"
                aria-labelledby="select-account-label"
                value={selectedAccountId}
                onChange={handleAccountChange}
                textMap={{
                  placeholderSingle: t(
                    "select.account.order.creation.placeholder"
                  ) as string,
                }}
              >
                {options.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {createDisplayNameForDealer(
                      option,
                      shouldDisplayDealerName
                    )}
                  </Option>
                ))}
              </Select>
            </Box>
            <FormHelperText data-testid="select-account-helper-text">
              {helperText}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={handleCloseModel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isContinueButtonDisable}
            onClick={handleOnClickContinue}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </DummyOrderCreationPage>
  );
};

export default SelectAccountForOrder;
